/**
 * The install's own item catalog: every pid the engine could be asked for, with
 * the name and the art the engine would show for it.
 *
 * Three files, each already understood by an existing codec, and reused rather
 * than re-derived:
 *
 *   - `proto/items/items.lst` — **1-based and positional** (`_proto_list_str`,
 *     engine/src/proto.cc:201). Line N is proto number N, and an item's pid is
 *     `OBJ_TYPE_ITEM << 24 | N` with `OBJ_TYPE_ITEM` = 0, so for items pid and
 *     line number are the same number. `lst-codec.ts` already parses this.
 *   - `proto/items/<file>.pro` — `proto-codec.ts` reads it: big-endian, with
 *     `messageId` and `type` in the common header and `inventoryFid` at the end
 *     of it. The `type` is the fact the save editor cannot do without, because
 *     it is what decides how many extra int32s an item writes into a save.
 *   - `text/<language>/game/pro_item.msg` — `msg-codec.ts` reads it. A proto's
 *     name is entry `messageId`, its description `messageId + 1`
 *     (`protoGetMessage`/`protoGetName`, proto.cc). We take the messageId from
 *     the proto rather than assuming the `100 × line` convention: the
 *     convention is what vanilla happens to do, not a rule the engine enforces.
 *
 * Every one of them comes through `GameFiles`, so a modded loose `data/` wins
 * over patch000.dat wins over master.dat — the same order the engine would use,
 * which is what makes the catalog match what the player would actually get.
 *
 * ## Refusals are entries too
 *
 * `items.lst` names files that are not in any archive (66 of 605 on a stock
 * install — unused lines and the filler the mod format reserves). Those are
 * skipped silently. A proto that *is* present but does not parse is also
 * skipped: a catalog row that hands the bridge a pid the engine will refuse is
 * worse than a missing row.
 */

import { getField, hasField, parseItemProto, readAllFields, type ItemProtoFile } from '../mods/proto-codec';
import { getText, parseMsg } from '../mods/msg-codec';
import { lookupProtoFile, parseLst } from '../mods/lst-codec';
import type { ItemTypeName } from '../mods/fields';
import type { GameFiles } from './files';

export interface CatalogItem {
  /** `OBJ_TYPE_ITEM << 24 | protoNumber`, which for items equals protoNumber. */
  pid: number;
  protoNumber: number;
  /** From `pro_item.msg`, or the proto's filename when the text is missing. */
  name: string;
  /** Entry `messageId + 1`. Empty when absent. */
  description: string;
  itemType: ItemTypeName;
  /** Inventory-slot art id. 0 when the proto does not carry one. */
  inventoryFid: number;
  fileName: string;
  /**
   * Every field the proto codec knows about, by its `fields.ts` name — the whole
   * common header plus this item's subtype block, already decoded.
   *
   * Kept because the proto is parsed here anyway and re-reading it later means
   * another inflate out of master.dat on a hover. It is ~25 numbers per item, so
   * holding it for all ~540 costs less than the FRM for one icon.
   */
  fields: Readonly<Record<string, number>>;
  /** Present for ITEM_TYPE_DRUG; values are from this install's winning proto. */
  drug?: DrugProtoData;
}

export interface DrugProtoData {
  stats: readonly [number, number, number];
  immediate: readonly [number, number, number];
  duration1: number;
  delayed1: readonly [number, number, number];
  duration2: number;
  delayed2: readonly [number, number, number];
  addictionChance: number;
  withdrawalEffect: number;
  withdrawalOnset: number;
}

export interface ItemCatalog {
  items: CatalogItem[];
  byPid: Map<number, CatalogItem>;
  /** True when `items.lst` was found at all. */
  ok: boolean;
  /** Which `pro_item.msg` supplied the names, for the panel's provenance line. */
  textPath: string | null;
}

/**
 * Where the item text might be, most specific first. The engine takes the
 * language from `[system] language` in fallout2.cfg and falls back to English;
 * the caller passes whatever it could read, and English is always tried last.
 */
const textCandidates = (language?: string | null): string[] => [
  ...(language && language.toLowerCase() !== 'english' ? [`text/${language.toLowerCase()}/game/pro_item.msg`] : []),
  'text/english/game/pro_item.msg',
];

const EMPTY: ItemCatalog = { items: [], byPid: new Map(), ok: false, textPath: null };

/**
 * Reads the whole catalog. One pass, and the result is meant to be held: on a
 * stock install this is ~540 archive members, each one a separate inflate.
 */
export async function loadItemCatalog(files: GameFiles, language?: string | null): Promise<ItemCatalog> {
  const lstBytes = await files.read('proto/items/items.lst');
  if (!lstBytes) return EMPTY;
  const lst = parseLst(lstBytes);

  // Names are optional: an install with no English text still gets a catalog,
  // it just falls back to filenames.
  let textPath: string | null = null;
  let msg: ReturnType<typeof parseMsg> | null = null;
  for (const path of textCandidates(language)) {
    const bytes = await files.read(path);
    if (!bytes) continue;
    try {
      msg = parseMsg(bytes);
      textPath = path;
      break;
    } catch {
      /* a .msg the engine would also reject; try the next */
    }
  }

  const items: CatalogItem[] = [];
  const byPid = new Map<number, CatalogItem>();

  for (let protoNumber = 1; protoNumber <= lst.lines.length; protoNumber++) {
    const fileName = lookupProtoFile(lst, protoNumber);
    if (!fileName) continue;
    const bytes = await files.read(`proto/items/${fileName}`);
    if (!bytes) continue;

    let proto: ItemProtoFile;
    try {
      proto = parseItemProto(bytes);
    } catch {
      continue;
    }
    // The pid in the file is the authority; a proto whose pid disagrees with
    // its line has been renumbered by hand and is not safe to offer.
    if (proto.protoNumber !== protoNumber) continue;

    const nameFromText = msg ? getText(msg, proto.messageId) : null;
    const description = msg ? getText(msg, proto.messageId + 1) : null;
    const item: CatalogItem = {
      pid: proto.pid,
      protoNumber,
      name: (nameFromText ?? '').trim() || fileName.replace(/\.pro$/i, ''),
      description: (description ?? '').trim(),
      itemType: proto.itemType,
      inventoryFid: hasField(proto, 'inventoryFid') ? getField(proto, 'inventoryFid') : 0,
      fileName,
      fields: readAllFields(proto),
    };
    if (proto.itemType === 'drug') {
      item.drug = {
        stats: [getField(proto, 'stat.0'), getField(proto, 'stat.1'), getField(proto, 'stat.2')],
        immediate: [getField(proto, 'amount.0'), getField(proto, 'amount.1'), getField(proto, 'amount.2')],
        duration1: getField(proto, 'duration1'),
        delayed1: [getField(proto, 'amount1.0'), getField(proto, 'amount1.1'), getField(proto, 'amount1.2')],
        duration2: getField(proto, 'duration2'),
        delayed2: [getField(proto, 'amount2.0'), getField(proto, 'amount2.1'), getField(proto, 'amount2.2')],
        addictionChance: getField(proto, 'addictionChance'),
        withdrawalEffect: getField(proto, 'withdrawalEffect'),
        withdrawalOnset: getField(proto, 'withdrawalOnset'),
      };
    }
    items.push(item);
    byPid.set(item.pid, item);
  }

  return { items, byPid, ok: true, textPath };
}
